import { inject, Injectable } from '@angular/core';
import { take, tap } from 'rxjs';

import { UserRoles } from '../../enums';
import {
  ReportCluster,
  Reports,
  Screens,
} from '../../enums/role-permissions.enum';
import { LocationService } from '../../services/admin/location/location.service';

@Injectable({
  providedIn: 'root',
})
export class RolePermissionsUtils {
  private locationService = inject(LocationService);

  showScreen(screen: Screens | string, role: string) {
    let showScreen = false;

    //All Operations screens have same permissions so check here
    if (screen.toString().includes(Screens.OPERATIONS)) {
      return this.get_hd_reports_retail(role);
    }

    switch (screen) {
      //All Users Can Access Home
      case Screens.HOME:
        return true;

      ///////Admin Screens///////
      case Screens.ADMIN:
        // excluded: REPORTS, USER, RETAIL
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.GROUPCOORDINATOR ||
          role === UserRoles.HD ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUP ||
          role === UserRoles.LOCMGR
        );

      case Screens.ADMIN_SEARCH:
        // excluded: REPORTS, GROUPMANAGER, GROUP, LOCMGR, USER, RETAIL
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.GROUPCOORDINATOR ||
          role === UserRoles.HD
        );

      case Screens.USER_ADMIN:
      case Screens.ROUTE_ADMIN:
      case Screens.ADL:
        // excluded: HD, REPORTS, USER, RETAIL
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.GROUPCOORDINATOR ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUP ||
          role === UserRoles.LOCMGR
        );

      case Screens.LOCATION_ADMIN:
        // excluded: HD, REPORTS, LOCMGR, USER, RETAIL
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.GROUPCOORDINATOR ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUP
        );

      case Screens.LOCATION_GROUP:
        // excluded: HD, REPORTS, GROUP, LOCMGR, USER, RETAIL
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.GROUPCOORDINATOR ||
          role === UserRoles.GROUPMANAGER
        );

      case Screens.DEVICE_ADMIN:
        this.locationService
          .getCurrentLocation$()
          .pipe(
            take(1),
            tap((currentData) => {
              if (currentData?.location) {
                // excluded: HD, REPORTS, GROUP, USER, RETAIL
                showScreen =
                  currentData.location.byodEnabled &&
                  (role === UserRoles.ADMIN ||
                    role === UserRoles.GROUPADMIN ||
                    role === UserRoles.GROUPCOORDINATOR ||
                    role === UserRoles.GROUPMANAGER ||
                    role === UserRoles.LOCMGR);
              }
            }),
          )
          .subscribe();
        return showScreen;

      case Screens.BUILDING_SCAN_ADMIN:
        // excluded: HD, REPORTS, GROUPMANAGER, GROUP, LOCMGR, USER, RETAIL
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.GROUPCOORDINATOR
        );

      ////////////////////////////
      ///////Exceptions Screen///////
      case Screens.EXCEPTIONS_GROUP:
        // excluded: HD, REPORTS
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUPCOORDINATOR ||
          role === UserRoles.GROUP ||
          role === UserRoles.LOCMGR ||
          role === UserRoles.USER ||
          role === UserRoles.RETAIL
        );

      case Screens.BUILDING_SCAN_EXCEPTIONS:
      case Screens.EXCEPTIONS:
      case Screens.PRINT_SHIPPING_LABEL:
      case Screens.TRACKINGNUMBERSEARCH:
        return this.get_hd_reports_retail(role);

      case Screens.INTERCEPTS:
        // excluded: HD, REPORTS
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.GROUPCOORDINATOR ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUP ||
          role === UserRoles.LOCMGR ||
          role === UserRoles.USER ||
          role === UserRoles.RETAIL
        );

      case Screens.REPORTS:
        // excluded: HD
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.GROUPCOORDINATOR ||
          role === UserRoles.REPORTS ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUP ||
          role === UserRoles.LOCMGR ||
          role === UserRoles.USER ||
          role === UserRoles.RETAIL
        );

      case Screens.FILE_UPLOAD:
        this.locationService
          .getCurrentLocation$()
          .pipe(
            take(1),
            tap((location) => {
              if (location.location?.fileUploadEnabled) {
                showScreen = location.location?.fileUploadEnabled;
              }
            }),
          )
          .subscribe();
        return this.get_hd_reports_retail(role) && showScreen;

      //All Users Can Access Logout
      case Screens.LOGOUT:
        return true;

      //Base url should return true so guard can redirect properly
      case '/':
        return true;

      default:
        return false;
    }
  }

  showReports(report: Reports | undefined, idoEnabled: boolean, role: string) {
    switch (report) {
      ////////////ONROAD OPERATIONS////////////
      case Reports.DRIVER_LOAD_REPORT:
      case Reports.DELIVERY_MANIFEST:
      case Reports.PICKUP_MANIFEST:
      case Reports.LOAD_DISCREPANCY_REPORT:
      case Reports.SEND_AGAIN:
      case Reports.DRY_ICE:
        // excluded: HD, RETAIL
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.REPORTS ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUPCOORDINATOR ||
          role === UserRoles.GROUP ||
          role === UserRoles.LOCMGR ||
          role === UserRoles.USER
        );

      /////////////////////////////
      ////////////CODS////////////
      case Reports.COD_OUT_FOR_DELIVERY:
      case Reports.COD_RECONCILE:
      case Reports.COD_RECONCILE_REPORT:
      case Reports.COD_COLLECTED_CLEARED:
      case Reports.COD_OUTSTANDING:
        // excluded: HD, RETAIL
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.REPORTS ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUPCOORDINATOR ||
          role === UserRoles.GROUP ||
          role === UserRoles.LOCMGR ||
          role === UserRoles.USER
        );

      case Reports.GROUP_COD_EXTRACT:
        return this.getGroupRoles(role);

      /////////////////////////////
      ////////////EXCEPTIONS REPORTS////////////
      case Reports.HANDOVER_SCAN_REPORT:
      case Reports.EXCEPTION_PROCESSING_REPORT:
      case Reports.BUILDING_SCAN_EXPORT:
        // excluded: HD, RETAIL
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.REPORTS ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUPCOORDINATOR ||
          role === UserRoles.GROUP ||
          role === UserRoles.LOCMGR ||
          role === UserRoles.USER
        );

      /////////////////////////////
      ////////////SUMMARIES////////////
      case Reports.LOCATION_SUMMARY:
        // excluded: HD, RETAIL
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.REPORTS ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUPCOORDINATOR ||
          role === UserRoles.GROUP ||
          role === UserRoles.LOCMGR ||
          role === UserRoles.USER
        );

      case Reports.GROUP_SUMMARY:
        return this.getGroupRoles(role);

      /////////////////////////////
      ////////////INVENTORY////////////
      case Reports.LOCATION_INVENTORY:
      case Reports.GROUP_INVENTORY:
        // excluded: HD, RETAIL
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.REPORTS ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUPCOORDINATOR ||
          role === UserRoles.GROUP ||
          role === UserRoles.LOCMGR ||
          role === UserRoles.USER
        );

      /////////////////////////////
      ////////////ERRORS////////////
      case Reports.LOCATION_ERROR_REPORT:
      case Reports.GROUP_ERROR_REPORT:
        // excluded: HD, RETAIL
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.REPORTS ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUPCOORDINATOR ||
          role === UserRoles.GROUP ||
          role === UserRoles.LOCMGR ||
          role === UserRoles.USER
        );

      /////////////////////////////
      ////////////OTHER////////////
      case Reports.AGED_PACKAGE_STATUS:
      case Reports.NON_UPS_BARCODE_EXTRACT:
      case Reports.PACKAGE_NOT_FOUND:
        // excluded: HD, RETAIL
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.REPORTS ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUPCOORDINATOR ||
          role === UserRoles.GROUP ||
          role === UserRoles.LOCMGR ||
          role === UserRoles.USER
        );

      case Reports.RETAIL_ACTIVITY_REPORT:
      case Reports.AGED_RETAIL_STATUS:
        // excluded: HD, USER
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.REPORTS ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUPCOORDINATOR ||
          role === UserRoles.GROUP ||
          role === UserRoles.LOCMGR ||
          role === UserRoles.RETAIL
        );

      /////////////////////////////
      ////////////IDO////////////
      case Reports.LOCATION_FORECAST:
      case Reports.LOCATION_EDW:
        // excluded: HD, RETAIL
        return (
          (role === UserRoles.ADMIN ||
            role === UserRoles.GROUPADMIN ||
            role === UserRoles.REPORTS ||
            role === UserRoles.GROUPMANAGER ||
            role === UserRoles.GROUPCOORDINATOR ||
            role === UserRoles.GROUP ||
            role === UserRoles.LOCMGR ||
            role === UserRoles.USER) &&
          idoEnabled
        );

      case Reports.GROUP_FORECAST:
      case Reports.GROUP_EDW:
        return this.getGroupRoles_IDO(role, idoEnabled);

      /////////////////////////////
      default:
        return false;
    }
  }

  showReportGroup(
    group: ReportCluster | undefined,
    idoEnabled: boolean,
    role: string,
  ) {
    switch (group) {
      case ReportCluster.ERRORS:
      case ReportCluster.INVENTORY:
      case ReportCluster.SUMMARIES:
      case ReportCluster.CODS:
      case ReportCluster.EXCEPTIONS_REPORTS:
      case ReportCluster.ONROAD_OPERATIONS:
        // excluded: HD, RETAIL
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.REPORTS ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUPCOORDINATOR ||
          role === UserRoles.GROUP ||
          role === UserRoles.LOCMGR ||
          role === UserRoles.USER
        );
      case ReportCluster.OTHER:
        // excluded: HD
        return (
          role === UserRoles.ADMIN ||
          role === UserRoles.GROUPADMIN ||
          role === UserRoles.GROUPCOORDINATOR ||
          role === UserRoles.REPORTS ||
          role === UserRoles.GROUPMANAGER ||
          role === UserRoles.GROUP ||
          role === UserRoles.LOCMGR ||
          role === UserRoles.USER ||
          role === UserRoles.RETAIL
        );
      case ReportCluster.IDO:
        // excluded: HD, RETAIL
        return (
          (role === UserRoles.ADMIN ||
            role === UserRoles.GROUPADMIN ||
            role === UserRoles.GROUPCOORDINATOR ||
            role === UserRoles.REPORTS ||
            role === UserRoles.GROUPMANAGER ||
            role === UserRoles.GROUP ||
            role === UserRoles.LOCMGR ||
            role === UserRoles.USER) &&
          idoEnabled
        );
      default:
        return false;
    }
  }

  canSeeUserProfile(role: string) {
    // excluded: HD
    return (
      role === UserRoles.ADMIN ||
      role === UserRoles.GROUPADMIN ||
      role === UserRoles.REPORTS ||
      role === UserRoles.GROUPMANAGER ||
      role === UserRoles.GROUPCOORDINATOR ||
      role === UserRoles.GROUP ||
      role === UserRoles.LOCMGR ||
      role === UserRoles.USER ||
      role === UserRoles.RETAIL ||
      ''
    );
  }

  canAddUsers(role: string) {
    // excluded:HD, REPORTS, GROUPMANAGER, GROUP, LOCMGR, USER, RETAIL
    return (
      role === UserRoles.ADMIN ||
      role === UserRoles.GROUPADMIN ||
      role === UserRoles.GROUPCOORDINATOR
    );
  }

  canEditAdminSearchUsers(role: string) {
    // excluded:HD, REPORTS, GROUPMANAGER, GROUP, LOCMGR, USER, RETAIL
    return (
      role === UserRoles.ADMIN ||
      role === UserRoles.GROUPADMIN ||
      role === UserRoles.GROUPCOORDINATOR
    );
  }

  canDeleteAdminSearchUsers(role: string) {
    // excluded:HD, REPORTS, GROUPMANAGER, GROUP, LOCMGR, USER, RETAIL
    return (
      role === UserRoles.ADMIN ||
      role === UserRoles.GROUPADMIN ||
      role === UserRoles.GROUPCOORDINATOR
    );
  }

  canEditDeleteUserAdminUsers(role: string) {
    // excluded: HD, REPORTS, USER, RETAIL
    return (
      role === UserRoles.ADMIN ||
      role === UserRoles.GROUPCOORDINATOR ||
      role === UserRoles.GROUPADMIN ||
      role === UserRoles.GROUPMANAGER ||
      role === UserRoles.GROUP ||
      role === UserRoles.LOCMGR
    );
  }

  canAddLocationAdmin(role: string) {
    // excluded: GROUPADMIN, GROUPCOORDINATOR, HD, REPORTS, GROUPMANAGER, GROUP, LOCMGR, USER, RETAIL
    return role === UserRoles.ADMIN;
  }

  canEditDeleteLocation(role: string): boolean {
    // excluded: HD, REPORTS, GROUPADMIN, GROUPMANAGER, GROUP, LOCMGR, USER, RETAIL
    return role === UserRoles.ADMIN || role === UserRoles.GROUPCOORDINATOR;
  }

  canChangeOwnLocation(role: string): boolean {
    // excluded: REPORTS, LOCMGR, USER, RETAIL
    return (
      role === UserRoles.ADMIN ||
      role === UserRoles.GROUPCOORDINATOR ||
      role === UserRoles.GROUPADMIN ||
      role === UserRoles.GROUPMANAGER ||
      role === UserRoles.GROUP ||
      role === UserRoles.HD
    );
  }

  canChangeOwnGroup(role: string): boolean {
    // excluded: REPORTS, GROUPMANAGER, GROUP, LOCMGR, USER, RETAIL
    return (
      role === UserRoles.ADMIN ||
      role === UserRoles.GROUPCOORDINATOR ||
      role === UserRoles.GROUPADMIN ||
      role === UserRoles.HD
    );
  }

  canAddEditDeleteDeviceAdmin(role: string): boolean {
    // excluded: HD, REPORTS, GROUP, LOCMGR, USER, RETAIL
    return (
      role === UserRoles.ADMIN ||
      role === UserRoles.GROUPADMIN ||
      role === UserRoles.GROUPMANAGER ||
      role === UserRoles.GROUPCOORDINATOR
    );
  }

  private getGroupRoles_IDO(role: string, idoEnabled: boolean): boolean {
    // excluded: HD, LOCMGR, USER, RETAIL
    return (
      (role === UserRoles.ADMIN ||
        role === UserRoles.GROUPADMIN ||
        role === UserRoles.REPORTS ||
        role === UserRoles.GROUPMANAGER ||
        role === UserRoles.GROUP ||
        role === UserRoles.GROUPCOORDINATOR) &&
      idoEnabled
    );
  }

  private getGroupRoles(role: string): boolean {
    // excluded: HD, LOCMGR, USER, RETAIL
    return (
      role === UserRoles.ADMIN ||
      role === UserRoles.GROUPADMIN ||
      role === UserRoles.GROUPCOORDINATOR ||
      role === UserRoles.REPORTS ||
      role === UserRoles.GROUPMANAGER ||
      role === UserRoles.GROUP
    );
  }

  private get_hd_reports_retail(role: string): boolean {
    // excluded: HD, REPORTS, RETAIL
    return (
      role === UserRoles.ADMIN ||
      role === UserRoles.GROUPADMIN ||
      role === UserRoles.GROUPCOORDINATOR ||
      role === UserRoles.GROUPMANAGER ||
      role === UserRoles.GROUP ||
      role === UserRoles.LOCMGR ||
      role === UserRoles.USER
    );
  }
}
